import type React from "react";

/**
 * Loading skeleton for {@link CompetitorComparisonChart}. Mirrors the chart shell
 * (container + header row + plot body) with shimmer bars so the page never pops
 * data in. Animation reuses the global `skeleton-shimmer-bar` class (which honors
 * `prefers-reduced-motion`); the staggered `--shimmer-delay` matches the Dashboard
 * KPI skeletons (see the `if (!charts)` block in MyBrandKpiChart).
 *
 * Presentational only — exposes `role="status"` + `aria-busy` so the shimmer is
 * announced to assistive tech instead of being silent.
 */
export function CompetitorComparisonChartSkeleton() {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading competitor comparison chart"
      data-testid="competitor-chart-skeleton"
      className="rounded-[14px] border border-border bg-card p-[21px]"
    >
      {/* Header: title bar + chart-type toggle placeholder. */}
      <div className="flex items-center justify-between gap-4">
        <div className="skeleton-shimmer-bar h-4 w-48 rounded-md" />
        <div
          className="skeleton-shimmer-bar h-8 w-36 rounded-full"
          style={{ "--shimmer-delay": "120ms" } as React.CSSProperties}
        />
      </div>

      {/* Plot body — matches the live chart's `h-[420px]` track. */}
      <div
        className="skeleton-shimmer-bar mt-4 h-[420px] w-full rounded-md"
        style={{ "--shimmer-delay": "240ms" } as React.CSSProperties}
      />
    </div>
  );
}
